import { MdOutlinePersonAddDisabled, MdPersonAddAlt1 } from "react-icons/md";
import { FILE_STORAGE_SERVER_URL } from "src/helpers/constants";
import { memo, useMemo, useState } from "react";
import {
  formatLongDate,
  getShortName,
  showNotification,
} from "src/helpers/utils";
import useUsers from "src/hooks/useUsers";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import useCache from "src/hooks/useCache";
import { useTranslation } from "react-i18next";
import useInvites from "src/hooks/useInvites";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import ContactCard, { EContactCardType } from "./ContactCard";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IReceivedContactInviteCard {
  id: string;
  user: string;
  index: number;
  date: string;
  vertical?: boolean;
  hideActions?: boolean;
}

const ReceivedContactInviteCard: React.FC<IReceivedContactInviteCard> = ({
  user,
  index,
  date,
  id,
  vertical = false,
  hideActions = false,
}) => {
  const { t } = useTranslation();
  const { clearCache } = useCache();
  const { getUserById } = useUsers();
  const { acceptContactInvite, deleteContactInvite, resetInvitesCache } =
    useInvites();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState<boolean>(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState<boolean>(false);

  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isRejecting, setIsRejecting] = useState<boolean>(false);

  const userInfo = useMemo(() => {
    return getUserById({ _id: user });
  }, [user]);

  const userImage = useMemo(() => {
    if (
      userInfo?.picture &&
      userInfo?.settings?.privacy?.seePicture === EPrivacyVisibility.EVERYONE
    ) {
      return `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`;
    }

    return null;
  }, [userInfo]);

  return (
    <>
      <ContactCard
        type={EContactCardType.SENT}
        actions={
          hideActions
            ? []
            : [
                {
                  text: vertical ? t("common:tooltips.accept") : "",
                  tooltip: vertical ? "" : t("common:tooltips.accept"),
                  icon: <MdPersonAddAlt1 size={ICON_SIZES.MD} />,
                  onClick: (e: any) => {
                    e?.preventDefault?.();
                    setShowAcceptDialog(true);
                  },
                  kind: EButtonKind.SAVE,
                },
              ]
        }
        index={index}
        subText={formatLongDate(date, true)}
        name={userInfo?.name}
        picture={userImage}
        onClick={() => setShowUserInfoModal(true)}
        vertical={vertical}
      />

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          onClose={() => setShowUserInfoModal(false)}
          actions={[
            {
              onClick: () => setShowRejectConfirm(true),
              icon: <MdOutlinePersonAddDisabled size={ICON_SIZES.MD} />,
              kind: EButtonKind.DANGER,
              tooltip: t("common:tooltips.reject"),
              isLoading: isRejecting,
            },
            {
              onClick: () => setShowAcceptDialog(true),
              icon: <MdPersonAddAlt1 size={ICON_SIZES.MD} />,
              kind: EButtonKind.SAVE,
              text: t("common:buttons.accept"),
              isLoading: isAccepting,
            },
          ]}
        />
      )}

      {showRejectConfirm && (
        <ConfirmDialog
          description="¿Deseas rechazar la solicitud?"
          title="Rechazar"
          onClose={() => setShowRejectConfirm(false)}
          onConfirm={async () => {
            setIsRejecting(true);
            const res = await deleteContactInvite(id);

            if (res?.data?.success) {
              showNotification({
                kind: "success",
                message: "Invitación rechazada",
              });
              await resetInvitesCache();
              setIsRejecting(false);
              setShowRejectConfirm(false);
              setShowUserInfoModal(false);
            } else {
              setIsRejecting(false);
              showNotification({
                kind: "error",
                message: "Error rechazando la invitación",
              });
            }
          }}
          doubleConfirmRequired
        />
      )}

      {showAcceptDialog && (
        <ConfirmDialog
          description={`¿Deseas aceptar la invitación de ${getShortName(
            userInfo?.name,
          )}?`}
          onConfirm={async () => {
            setIsAccepting(true);
            const res = await acceptContactInvite(id);

            if (res?.data?.success) {
              showNotification({
                kind: "success",
                message: "Invitación aceptada",
              });
              await resetInvitesCache();
              setIsAccepting(false);
              setShowAcceptDialog(false);
              setShowUserInfoModal(false);
            } else {
              setIsAccepting(false);
              showNotification({
                kind: "error",
                message: "Error aceptando la invitación",
              });
            }
          }}
          onClose={() => setShowAcceptDialog(false)}
          confirmKind={EButtonKind.SAVE}
          title="Aceptar"
          isLoading={isAccepting}
        />
      )}
    </>
  );
};

export default memo(ReceivedContactInviteCard);
